
import { useCallback, useEffect, useRef } from 'react';
import { Background, Controls, ReactFlow } from '@xyflow/react';

const getFitViewOptions = (isLargeScreen) => ({
  padding: isLargeScreen ? 0.12 : 0.1,
  minZoom: isLargeScreen ? 0.55 : 0.35,
  maxZoom: 1.2,
});

export default function ThinkGraph({
  syncCurrentId,
  syncFlowNodes,
  syncFlowEdges,
  syncNodeTypes,
  syncGraph,
  handleSyncNodeClick,
  handleDrillDown,
  isLargeScreen,
  setSelectedNoteId,
  setSelectedAssetId,
  setSelectedInsightId,
  openSinkNotes,
  openSyncWorkspace,
  handleDiscussThinkNode,
  setGlobalSelectedTags,
  setSyncViewMode,
}) {
  const containerRef = useRef(null);
  const reactFlowInstanceRef = useRef(null);
  const fitViewFrameRef = useRef(null);
  const nodePanStateRef = useRef({
    active: false,
    lastPointer: null,
    didPan: false,
    suppressClick: false,
  });

  const getPointerPosition = (event) => {
    const source = event?.nativeEvent || event;
    const touch = source?.touches?.[0] || source?.changedTouches?.[0];
    const x = Number(touch?.clientX ?? source?.clientX);
    const y = Number(touch?.clientY ?? source?.clientY);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }

    return { x, y };
  };

  const fitRootView = useCallback((duration = 350) => {
    if (!reactFlowInstanceRef.current || syncCurrentId !== 'root' || syncFlowNodes.length === 0) {
      return;
    }

    reactFlowInstanceRef.current.fitView({
      ...getFitViewOptions(isLargeScreen),
      duration,
    });
  }, [isLargeScreen, syncCurrentId, syncFlowNodes.length]);

  useEffect(() => {
    const element = containerRef.current;

    const handlePointerDown = (event) => {
      if (typeof event?.button === 'number' && event.button !== 0) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const pressedNode = target.closest('.react-flow__node');
      if (!pressedNode) {
        return;
      }

      const pointer = getPointerPosition(event);
      if (!pointer) {
        nodePanStateRef.current.active = false;
        nodePanStateRef.current.lastPointer = null;
        return;
      }

      nodePanStateRef.current.active = true;
      nodePanStateRef.current.didPan = false;
      nodePanStateRef.current.lastPointer = pointer;
    };

    const handlePointerMove = (event) => {
      const panState = nodePanStateRef.current;

      if (!panState.active || !reactFlowInstanceRef.current || !panState.lastPointer) {
        return;
      }

      const pointer = getPointerPosition(event);
      if (!pointer) {
        return;
      }

      const dx = pointer.x - panState.lastPointer.x;
      const dy = pointer.y - panState.lastPointer.y;
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
        return;
      }

      if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
        panState.didPan = true;
      }

      const viewport = reactFlowInstanceRef.current.getViewport();
      const currentX = Number(viewport?.x);
      const currentY = Number(viewport?.y);
      const currentZoom = Number(viewport?.zoom);

      if (!Number.isFinite(currentX) || !Number.isFinite(currentY) || !Number.isFinite(currentZoom)) {
        reactFlowInstanceRef.current.setViewport({ x: 0, y: 0, zoom: 1 });
        panState.lastPointer = pointer;
        return;
      }

      reactFlowInstanceRef.current.setViewport({
        x: currentX + dx,
        y: currentY + dy,
        zoom: currentZoom,
      });

      panState.lastPointer = pointer;
    };

    const handlePointerUp = () => {
      const panState = nodePanStateRef.current;

      if (!panState.active) {
        return;
      }

      panState.active = false;
      panState.lastPointer = null;

      if (panState.didPan) {
        panState.suppressClick = true;
        requestAnimationFrame(() => {
          panState.suppressClick = false;
        });
      }

      panState.didPan = false;
    };

    element?.addEventListener('pointerdown', handlePointerDown, { capture: true });
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      element?.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  useEffect(() => {
    if (reactFlowInstanceRef.current && syncFlowNodes.length > 0) {
      // Only auto-fit when at root or when large screen state changes
      // This prevents the view from jumping when navigating into tags
      if (syncCurrentId === 'root') {
        fitRootView(600);
      }
    }
  }, [fitRootView, syncCurrentId, isLargeScreen, syncFlowNodes.length]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (fitViewFrameRef.current) {
        cancelAnimationFrame(fitViewFrameRef.current);
      }

      fitViewFrameRef.current = requestAnimationFrame(() => {
        fitRootView(250);
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (fitViewFrameRef.current) {
        cancelAnimationFrame(fitViewFrameRef.current);
        fitViewFrameRef.current = null;
      }
    };
  }, [fitRootView, syncCurrentId, isLargeScreen, syncFlowNodes.length]);

  const isEmpty = syncFlowNodes.length === 0 && syncCurrentId === 'root';

  return (
    <div ref={containerRef} className="relative min-h-0 flex-1 overflow-hidden">
      {isEmpty && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-6 text-center">
          <div className="max-w-md">
            <div className="text-4xl mb-4">🧠</div>
            <h3 className="text-lg font-bold text-white mb-2">Think Map</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Assetが登録されていません。HintからAssetを登録して、知識の繋がりを構築しましょう。
            </p>
            <button
              onClick={() => openSyncWorkspace('insights')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold py-2.5 px-6 rounded-full transition-all shadow-lg shadow-indigo-500/20"
            >
              Hintを確認する
            </button>
          </div>
        </div>
      )}
      <ReactFlow
        key={syncCurrentId}
        nodes={syncFlowNodes}
        edges={syncFlowEdges}
        nodeTypes={syncNodeTypes}
        nodesDraggable={false}
        fitView
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance;
          requestAnimationFrame(() => fitRootView(0));
        }}
        fitViewOptions={getFitViewOptions(isLargeScreen)}
        onNodeClick={(event, node) => {
          const panState = nodePanStateRef.current;
          if (panState.active || panState.suppressClick) {
            return;
          }

          const sourceNode = syncGraph.nodesById.get(node.id);
          if (!sourceNode) return;

          // Check if action buttons were clicked
          const isOpenButton = event?.target?.closest?.('.open-drill-down');
          const isDiscussButton = event?.target?.closest?.('.discuss-node');

          if (isDiscussButton) {
            handleDiscussThinkNode(sourceNode);
            return;
          }

          if (isOpenButton) {
            // "OPEN" or "LIST" button: Navigates to actual workspace data
            if (sourceNode.type === 'tag') {
              // LIST button for tags: filters by this tag and switches to List view
              setGlobalSelectedTags([sourceNode.tagName]);
              setSyncViewMode('list');
            } else if (sourceNode.type === 'note' || sourceNode.type === 'raw') {
              setSelectedNoteId(sourceNode.entityId);
              openSinkNotes();
            } else if (sourceNode.type === 'asset') {
              setSelectedAssetId(sourceNode.entityId);
              openSyncWorkspace('assets');
            } else if (sourceNode.type === 'insight') {
              setSelectedInsightId(sourceNode.entityId);
              openSyncWorkspace('insights');
            }
            return;
          }

          // Node body: Standard graph navigation
          if (sourceNode.id === syncCurrentId) {
            // Clicking active center node drills up
            handleSyncNodeClick(sourceNode);
          } else {
            // Clicking child node drills down
            handleSyncNodeClick(sourceNode);
            if (handleDrillDown) {
              handleDrillDown(sourceNode);
            }
          }
        }}
        nodesConnectable={false}
        elementsSelectable={false}
        selectNodesOnDrag={false}
        panOnDrag={[0, 1]}
        proOptions={{ hideAttribution: true }}
        className="bg-slate-950"
      >
        <Background color="#1e293b" gap={40} />
        <Controls 
          position="bottom-left"
          showZoom={false}
          showInteractive={false}
          className="!mb-4 !ml-4 !border-slate-800 !bg-slate-900/50 !shadow-2xl [&_button]:!bg-slate-800 [&_button]:!border-slate-700 [&_button]:!fill-slate-300 hover:[&_button]:!bg-slate-700 transition-colors"
        />
      </ReactFlow>
    </div>
  );
}
